import {
  getDefaultStoreCode,
  getConfiguredStores,
  getLocationOptions,
  getLocationByCode,
  getPrimaryLocationCodeForStore,
  getStoreCodeForLocation,
  getStoreOptions,
} from './appConfig';

export const ERP_STORE_SCOPE_KEY = 'erpActiveStoreScope';

function readStoredAuthUser() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem('auth-storage');
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    const state = parsed?.state || parsed;
    return state?.user || null;
  } catch (error) {
    return null;
  }
}

export function getAllowedStoreScopeOptions(user = readStoredAuthUser()) {
  const locationOptions = getLocationOptions();
  const storeOptions = getStoreOptions();
  const baseOptions = locationOptions.length > 0 ? locationOptions : storeOptions;
  const allScopeOptions = baseOptions.length > 1
    ? [{ value: 'all', label: 'Tout' }, ...baseOptions]
    : baseOptions;

  if (!user) {
    return allScopeOptions;
  }

  if (Array.isArray(user.accessible_locations) && user.accessible_locations.length > 0 && locationOptions.length > 0) {
    return allScopeOptions.filter((option) => (
      option.value === 'all' || user.accessible_locations.includes(option.value)
    ));
  }

  if (user.can_view_all_stores || user.store_access === 'both') {
    return allScopeOptions;
  }

  if (Array.isArray(user.accessible_stores) && user.accessible_stores.length > 0) {
    return allScopeOptions.filter((option) => (
      option.value === 'all'
      || user.accessible_stores.includes(option.value)
      || user.accessible_stores.includes(getStoreCodeForLocation(option.value))
    ));
  }

  if (user.store_access && allScopeOptions.some((option) => option.value === user.store_access)) {
    return allScopeOptions.filter((option) => option.value === user.store_access);
  }

  return allScopeOptions;
}

export function getDefaultStoreScope(user = readStoredAuthUser()) {
  const locationOptions = getLocationOptions();
  const defaultStoreCode = getDefaultStoreCode('all');
  const defaultLocationCode = getPrimaryLocationCodeForStore(defaultStoreCode, defaultStoreCode);
  const defaultScopeCode = locationOptions[0]?.value || defaultLocationCode || defaultStoreCode;
  const hasMultipleScopes = locationOptions.length > 1 || getStoreOptions().length > 1;

  if (!user) {
    return hasMultipleScopes ? 'all' : (defaultScopeCode || 'all');
  }

  if (Array.isArray(user.accessible_locations) && user.accessible_locations.length > 0) {
    return user.accessible_locations[0];
  }

  if (Array.isArray(user.accessible_stores) && user.accessible_stores.length > 0) {
    const accessibleLocation = locationOptions.find((option) => (
      user.accessible_stores.includes(getStoreCodeForLocation(option.value))
    ));
    return accessibleLocation?.value || user.accessible_stores[0];
  }

  if (user.can_view_all_stores || user.store_access === 'both') {
    return hasMultipleScopes ? 'all' : (defaultScopeCode || 'all');
  }

  if (user.store_access && getStoreOptions().some((option) => option.value === user.store_access)) {
    return getPrimaryLocationCodeForStore(user.store_access, user.store_access);
  }

  return defaultScopeCode || 'all';
}

export function normalizeStoreScope(value) {
  if (value === 'all') {
    return value;
  }

  if (getStoreOptions().some((option) => option.value === value)) {
    return value;
  }

  if (getLocationByCode(value, { includeDomicile: false })) {
    return value;
  }

  return '';
}

export function readStoreScope() {
  if (typeof window === 'undefined') {
    return getDefaultStoreScope();
  }

  const normalizedValue = normalizeStoreScope(window.localStorage.getItem(ERP_STORE_SCOPE_KEY));
  const allowedValues = new Set(getAllowedStoreScopeOptions().map((option) => option.value));

  if (allowedValues.has(normalizedValue)) {
    return normalizedValue;
  }

  const fallbackValue = getDefaultStoreScope();
  window.localStorage.setItem(ERP_STORE_SCOPE_KEY, fallbackValue);
  return fallbackValue;
}

export function writeStoreScope(value) {
  if (typeof window === 'undefined') {
    return;
  }

  const normalizedValue = normalizeStoreScope(value);
  const allowedValues = new Set(getAllowedStoreScopeOptions().map((option) => option.value));
  const nextValue = allowedValues.has(normalizedValue) ? normalizedValue : getDefaultStoreScope();

  window.localStorage.setItem(ERP_STORE_SCOPE_KEY, nextValue);
  window.dispatchEvent(new CustomEvent('erp-store-scope-change', {
    detail: nextValue,
  }));
}

export function mapStoreScopeToAppointmentLocation(value) {
  if (getLocationByCode(value, { includeDomicile: true })) {
    return value;
  }

  if (value && value !== 'all') {
    return getPrimaryLocationCodeForStore(value, 'all');
  }

  return 'all';
}

function getLocationCodesForStoreScope(storeScope) {
  if (getLocationByCode(storeScope, { includeDomicile: true })) {
    return [storeScope];
  }

  const store = getConfiguredStores().find((entry) => entry.code === storeScope);
  if (!store || !Array.isArray(store.localites)) {
    return [];
  }

  return store.localites
    .map((location) => String(location?.code || '').trim())
    .filter(Boolean);
}

export function getStoreScopeCodes(storeScope) {
  const normalizedScope = String(storeScope || '').trim();
  if (!normalizedScope || normalizedScope === 'all') {
    return [];
  }

  const codes = [normalizedScope];
  const parentStoreCode = getStoreCodeForLocation(normalizedScope);
  if (parentStoreCode) {
    codes.push(parentStoreCode);
  }

  const store = getConfiguredStores().find((entry) => entry.code === normalizedScope);
  if (store && Array.isArray(store.localites)) {
    store.localites.forEach((location) => {
      const locationCode = String(location?.code || '').trim();
      if (locationCode) {
        codes.push(locationCode);
      }
    });
  }

  return [...new Set(codes.filter(Boolean))];
}

export function storeMatchesScope(storeCode, storeScope) {
  if (!storeScope || storeScope === 'all') {
    return true;
  }

  const normalizedStoreCode = String(storeCode || '').trim();
  return Boolean(normalizedStoreCode && getStoreScopeCodes(storeScope).includes(normalizedStoreCode));
}

export function getScopedDefaultStore(storeScope = readStoreScope(), fallback = getDefaultStoreCode('')) {
  if (storeScope === 'all') {
    return fallback || getDefaultStoreCode('');
  }

  return getStoreCodeForLocation(storeScope) || storeScope;
}

export function getScopedStoreOptions(
  storeScope = readStoreScope(),
  { includeAll = false, includeCentral = false } = {}
) {
  const options = [];
  const localStoreOptions = getStoreOptions();

  if (includeAll) {
    options.push({ value: 'all', label: 'Tout' });
  }

  if (includeCentral) {
    options.push({ value: 'central', label: 'Stock central' });
  }

  if (storeScope !== 'all') {
    const scopedStoreCode = getStoreCodeForLocation(storeScope) || storeScope;
    const matchingOption = localStoreOptions.find((option) => option.value === scopedStoreCode);
    return matchingOption ? [...options, matchingOption] : options;
  }

  return [...options, ...localStoreOptions];
}

export function getScopedDefaultAppointmentLocation(storeScope = readStoreScope()) {
  const user = readStoredAuthUser();
  const accessibleLocations = Array.isArray(user?.accessible_locations) ? user.accessible_locations : [];

  if (getLocationByCode(storeScope, { includeDomicile: true })) {
    return storeScope;
  }

  if (storeScope !== 'all' && accessibleLocations.length > 0) {
    const matchingLocation = accessibleLocations.find(
      (locationCode) => getStoreCodeForLocation(locationCode) === storeScope
    );
    if (matchingLocation) {
      return matchingLocation;
    }
  }

  if (accessibleLocations.length > 0 && storeScope === 'all') {
    return accessibleLocations[0];
  }

  const location = mapStoreScopeToAppointmentLocation(storeScope);
  if (location !== 'all') {
    return location;
  }

  return getPrimaryLocationCodeForStore(getDefaultStoreCode(''), '');
}

export function getScopedAppointmentLocationOptions(
  storeScope = readStoreScope(),
  { includeAll = false, includeDomicile = false } = {}
) {
  const options = [];
  const user = readStoredAuthUser();

  if (includeAll) {
    options.push({ value: 'all', label: 'Toutes les localités' });
  }

  const accessibleLocations = Array.isArray(user?.accessible_locations) ? user.accessible_locations : [];
  const localLocationOptions = getLocationOptions({ includeDomicile }).filter((option) => (
    accessibleLocations.length === 0 || accessibleLocations.includes(option.value)
  ));
  const scopedLocationCodes = getLocationCodesForStoreScope(storeScope);

  if (scopedLocationCodes.length > 0) {
    return [
      ...options,
      ...localLocationOptions.filter((option) => scopedLocationCodes.includes(option.value)),
    ];
  }

  const scopedLocation = mapStoreScopeToAppointmentLocation(storeScope);
  if (scopedLocation !== 'all') {
    const matchingOption = localLocationOptions.find((option) => option.value === scopedLocation);
    return matchingOption ? [...options, matchingOption] : options;
  }

  return [...options, ...localLocationOptions];
}

export function mapAppointmentLocationToStoreScope(value) {
  return getStoreCodeForLocation(value) || getDefaultStoreCode('');
}
